import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { updateFamilyData } from '@/store/slices/formSlice'
import { FormSection } from '@/types/FormSectionInterface'
import { FamilyHistoryData } from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react'

interface FamilyHistoryProps {
  initialData?: Record<string, unknown>
  onDataChange?: (data: FamilyHistoryData) => void
  onComplete?: (isComplete: boolean) => void
}

interface RelativeCheckboxProps {
  id: string
  name: string
  label: string
  checked: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const RelativeCheckbox: React.FC<RelativeCheckboxProps> = ({ id, name, label, checked, onChange }) => {
  return (
    <label className="flex cursor-pointer items-center rounded-md border border-gray-300 p-3 transition-colors hover:bg-gray-50">
      <input
        type="checkbox"
        id={id}
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
      />
      <span className="ml-2 cursor-pointer text-sm text-gray-700">{label}</span>
    </label>
  )
}

const FamilyHistory = forwardRef<FormSection, FamilyHistoryProps>(({ initialData, onDataChange, onComplete }, ref) => {
  const dispatch = useAppDispatch()
  const { formData } = useAppSelector((state) => state.form)

  // Use Redux state as the source of truth, fallback to props for initial data
  const familyData = useMemo(() => {
    // If Redux state exists, use it
    if (formData.family) {
      return formData.family
    }

    // If no Redux state but we have initial data, use that
    if (initialData && Object.keys(initialData).length > 0) {
      return {
        familyHistory: String(initialData?.familyHistory || ''),
        hasSibling: Boolean(initialData?.hasSibling || false),
        hasParent: Boolean(initialData?.hasParent || false),
        hasGrandparent: Boolean(initialData?.hasGrandparent || false),
        hasCousin: Boolean(initialData?.hasCousin || false),
      }
    }

    // Fallback to default empty values
    return {
      familyHistory: '',
      hasSibling: false,
      hasParent: false,
      hasGrandparent: false,
      hasCousin: false,
    }
  }, [formData.family, initialData])

  // Initialize Redux state with initial data if provided
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0 && !formData.family) {
      const initialFamilyData = {
        familyHistory: String(initialData?.familyHistory || ''),
        hasSibling: Boolean(initialData?.hasSibling || false),
        hasParent: Boolean(initialData?.hasParent || false),
        hasGrandparent: Boolean(initialData?.hasGrandparent || false),
        hasCousin: Boolean(initialData?.hasCousin || false),
      }
      dispatch(updateFamilyData(initialFamilyData))
      onDataChange?.(initialFamilyData)
    }
  }, [initialData, formData.family, dispatch, onDataChange])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    const updateData = { [name]: value }

    // Update Redux state
    dispatch(updateFamilyData(updateData))

    // Also call the callback for backward compatibility
    const newData = { ...familyData, ...updateData }
    onDataChange?.(newData)

    // Check completion status after data change
    const newIsComplete =
      newData.familyHistory.trim() !== '' &&
      (newData.hasParent || newData.hasGrandparent || newData.hasSibling || newData.hasCousin)
    onComplete?.(newIsComplete)
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    const updateData = { [name]: checked }

    // Update Redux state
    dispatch(updateFamilyData(updateData))

    // Also call the callback for backward compatibility
    const newData = { ...familyData, ...updateData }
    onDataChange?.(newData)

    // Check completion status after data change
    const newIsComplete =
      newData.familyHistory.trim() !== '' &&
      (newData.hasParent || newData.hasGrandparent || newData.hasSibling || newData.hasCousin)
    onComplete?.(newIsComplete)
  }

  // Implement the FormSection interface
  useImperativeHandle(ref, () => ({
    isSectionComplete: () => {
      return FormCompletionChecker.isFamilyComplete(familyData)
    },
    renderSection: () => {
      // Return the main component render to avoid duplication
      return <FamilyHistory />
    },
    renderSectionInfo: () => {
      return <div></div>
    },
  }))

  return (
    <div className="space-y-4">
      <div>
        <label className="form-label mb-2 block">Is anyone in the family presenting key phenotype(s)?</label>
        <p className="mb-3 text-xs text-gray-400">Check all that apply</p>
        <div className="grid grid-cols-2 gap-4">
          <RelativeCheckbox
            id="hasParent"
            name="hasParent"
            label="Parent"
            checked={familyData.hasParent}
            onChange={handleCheckboxChange}
          />
          <RelativeCheckbox
            id="hasGrandparent"
            name="hasGrandparent"
            label="Grandparent"
            checked={familyData.hasGrandparent}
            onChange={handleCheckboxChange}
          />
          <RelativeCheckbox
            id="hasSibling"
            name="hasSibling"
            label="Sibling"
            checked={familyData.hasSibling}
            onChange={handleCheckboxChange}
          />
          <RelativeCheckbox
            id="hasCousin"
            name="hasCousin"
            label="Cousin"
            checked={familyData.hasCousin}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div>
        <label htmlFor="familyHistory" className="form-label mb-1 block">
          Family History <span className="text-red-500">*</span>
        </label>
        <textarea
          id="familyHistory"
          name="familyHistory"
          value={familyData.familyHistory}
          onChange={handleInputChange}
          rows={12}
          style={{ resize: 'none' }}
          required
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Enter the patient's family history here."
        />
      </div>
    </div>
  )
})

export default FamilyHistory
